import { useCallback, useEffect, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import { fetchWithAuth } from "@/lib/utils";
import type { ConnectionStatusData, ScopesTier } from "./use-connection-status";
import { type Me, useMe } from "./use-me";
import { ms365AuthorizationBody } from "./use-ms365-onboarding";
import { useTranslation } from "react-i18next";

export interface Ms365Connection {
  id: string;
  tenant_id?: string;
  user_oid: string;
  user_email?: string | null;
  workos_user_id?: string | null;
  connection_level?: Ms365ConnectionLevel;
  created_by_workos_user_id?: string | null;
  label?: string | null;
  status: string;
  scopes: string[];
  entra_tenant_id?: string;
  created_at?: string;
  updated_at?: string;
  last_sync_at?: string | null;
  last_refreshed_at?: string | null;
}

export interface ConnectionsData {
  connections: Ms365Connection[];
  admin_consent_granted?: boolean;
  organization_connections?: Ms365Connection[];
  capabilities?: { connection_levels?: boolean };
  can_manage_organization?: boolean;
}

export type Ms365ConnectionLevel = "user" | "organization";

export function supportsMs365ConnectionLevels(data: ConnectionsData | null | undefined): boolean {
  return data?.capabilities?.connection_levels === true;
}

export function getMs365OrganizationConnections(data: ConnectionsData | null | undefined): Ms365Connection[] {
  if (!supportsMs365ConnectionLevels(data)) return [];
  return (data?.organization_connections ?? []).filter((connection) => connection.workos_user_id === null);
}

type Ms365ConnectionUser = Pick<Me, "email" | "user_id" | "tenant">;

export type Ms365ConnectionMatchState = "matched" | "not_connected" | "unresolved";

export interface Ms365ConnectionResolution {
  connections: Ms365Connection[];
  matchState: Ms365ConnectionMatchState;
}

function normalizedEmail(email: string | null | undefined): string | null {
  const normalized = email?.trim().toLowerCase();
  return normalized || null;
}

function normalizedIdentifier(identifier: string | null | undefined): string | null {
  const normalized = identifier?.trim();
  return normalized || null;
}

function isConnectionInUserTenant(
  connection: Ms365Connection,
  me: Ms365ConnectionUser,
): boolean {
  const connectionTenant = normalizedIdentifier(connection.tenant_id);
  return connectionTenant === null || connectionTenant === normalizedIdentifier(me.tenant);
}

type OwnershipMatch = "match" | "other_user" | "unknown";

function connectionOwnership(
  connection: Ms365Connection,
  me: Ms365ConnectionUser,
): OwnershipMatch {
  const connectionUserId = normalizedIdentifier(connection.workos_user_id);
  const currentUserId = normalizedIdentifier(me.user_id);

  if (connectionUserId !== null) {
    if (currentUserId === null) return "unknown";
    return connectionUserId === currentUserId ? "match" : "other_user";
  }

  const connectionEmail = normalizedEmail(connection.user_email);
  const userEmail = normalizedEmail(me.email);
  if (connectionEmail !== null && connectionEmail === userEmail) return "match";

  // A connection without its authoritative WorkOS owner cannot safely be
  // assigned to another user merely because its Entra email differs. WorkOS
  // and Microsoft addresses are allowed to be different in the connect flow.
  return "unknown";
}

/**
 * Returns whether a tenant-scoped Microsoft 365 connection belongs to the
 * signed-in user. Older connections may not have a WorkOS user ID, so use
 * the email address as a case-insensitive fallback only when that ID is
 * absent.
 */
export function isMs365ConnectionForUser(
  connection: Ms365Connection,
  me: Ms365ConnectionUser | null | undefined,
): boolean {
  return Boolean(
    me
    && isConnectionInUserTenant(connection, me)
    && connectionOwnership(connection, me) === "match"
  );
}

/** Returns only the Microsoft 365 connections owned by the signed-in user. */
export function getMs365ConnectionsForUser(
  connections: Ms365Connection[] | null | undefined,
  me: Ms365ConnectionUser | null | undefined,
): Ms365Connection[] {
  return (connections ?? []).filter((connection) => isMs365ConnectionForUser(connection, me));
}

/**
 * Resolves the current user's connections without turning missing ownership
 * data into a false "not connected" result.
 *
 * A non-matching WorkOS owner is authoritative, so it is safe to exclude. A
 * null owner plus a different/null Entra email is not authoritative: the two
 * identity providers do not guarantee the same email address. If no positive
 * match exists in that situation, callers must render an unresolved state.
 */
export function resolveMs365ConnectionsForUser(
  connections: Ms365Connection[] | null | undefined,
  me: Ms365ConnectionUser | null | undefined,
): Ms365ConnectionResolution {
  if (!me) return { connections: [], matchState: "unresolved" };

  const candidates = (connections ?? []).filter((connection) =>
    isConnectionInUserTenant(connection, me)
  );
  const ownership = candidates.map((connection) => ({
    connection,
    match: connectionOwnership(connection, me),
  }));
  const matches = ownership
    .filter(({ match }) => match === "match")
    .map(({ connection }) => connection);

  if (matches.length > 0) {
    return { connections: matches, matchState: "matched" };
  }

  return {
    connections: [],
    matchState: ownership.some(({ match }) => match === "unknown")
      ? "unresolved"
      : "not_connected",
  };
}

export class Ms365ConnectionError extends Error {
  constructor(message: string, readonly code?: string) {
    super(message);
    this.name = "Ms365ConnectionError";
  }
}

export interface UseConnectionsReturn {
  data: ConnectionsData | null;
  loading: boolean;
  error: string | null;
  me: Me | null;
  meLoading: boolean;
  refresh: () => Promise<void>;
  connect: (scopes?: string[], userEmail?: string, connectionLevel?: Ms365ConnectionLevel) => Promise<string | null>;
  disconnect: (userOid: string) => Promise<boolean>;
  generateOnboardingLink: (
    entraTenantId?: string,
    customerName?: string,
    scopes?: string[],
  ) => Promise<string | null>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

function responseError(body: unknown, status: number): Ms365ConnectionError {
  if (!isRecord(body)) return new Ms365ConnectionError(`HTTP ${status}`);

  let detail = body.detail ?? body.error ?? body.message;
  let code = typeof body.code === "string" ? body.code : undefined;

  // Accept both the bridge's top-level error body and FastAPI's nested
  // HTTPException detail shape should another route return it directly.
  if (isRecord(detail)) {
    code ??= typeof detail.code === "string" ? detail.code : undefined;
    detail = detail.detail ?? detail.message ?? detail.error;
  }

  return new Ms365ConnectionError(
    typeof detail === "string" ? detail : `HTTP ${status}`,
    code,
  );
}

export function useConnections(getToken: () => Promise<string>): UseConnectionsReturn {
  const { t } = useTranslation();
  const [data, setData] = useState<ConnectionsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth("/api/ms365/connections", { headers: {} }, getToken);
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const body = await res.json();
      setData(body as ConnectionsData);
    } catch (err: any) {
      setError(err?.message ?? t("connections:ms365.couldNotLoadStatus"));
    } finally {
      setLoading(false);
    }
  }, [getToken, t]);

  const { me, loading: meLoading } = useMe(getToken);

  const connect = useCallback(
    async (scopes?: string[], userEmail?: string, connectionLevel?: Ms365ConnectionLevel): Promise<string | null> => {
      setError(null);
      try {
        const res = await fetchWithAuth(
          "/api/ms365/auth-url",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(ms365AuthorizationBody(userEmail ?? me?.email, scopes, connectionLevel)),
          },
          getToken,
        );
        if (!res.ok) {
          const body: unknown = await res.json().catch(() => ({}));
          throw responseError(body, res.status);
        }
        const body = await res.json();
        return body.url ?? null;
      } catch (err: unknown) {
        if (err instanceof Ms365ConnectionError && err.code) {
          if (err.code === "ms365_organization_unsupported") await refresh();
          err.message = t(`connections:ms365.errors.${err.code}`, { defaultValue: err.message });
        }
        setError(err instanceof Error ? err.message : t("connections:ms365.failedToGenerateSignInUrl"));
        throw err;
      }
    },
    [getToken, me?.email, refresh, t],
  );

  const disconnect = useCallback(async (userOid: string): Promise<boolean> => {
    setError(null);
    try {
      const res = await fetchWithAuth("/api/ms365/offboarding", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_oid: userOid }),
      }, getToken);
      if (!res.ok) throw responseError(await res.json().catch(() => ({})), res.status);
      await refresh();
      return true;
    } catch (err) {
      if (err instanceof Ms365ConnectionError && err.code) {
        err.message = t(`connections:ms365.errors.${err.code}`, { defaultValue: err.message });
      }
      setError(err instanceof Error ? err.message : t("connections:ms365.requestFailed"));
      return false;
    }
  }, [getToken, refresh, t]);

  const generateOnboardingLink = useCallback(
    async (
      entraTenantId?: string,
      customerName?: string,
      scopes?: string[],
    ): Promise<string | null> => {
      try {
        const res = await fetchWithAuth(
          "/api/ms365/onboarding/link",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...(entraTenantId && { entra_tenant_id: entraTenantId }),
              ...(customerName && { customer_name: customerName }),
              ...(scopes && scopes.length > 0 ? { scopes } : {}),
            }),
          },
          getToken,
        );
        if (!res.ok) {
          const body: unknown = await res.json().catch(() => ({}));
          throw responseError(body, res.status);
        }
        const body = await res.json();
        return body.url ?? null;
      } catch (err: any) {
        setError(err?.message ?? t("connections:ms365.failedToGenerateOnboardingLink"));
        return null;
      }
    },
    [getToken, t],
  );

  useRefreshOnReconnect(refresh);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    data,
    loading,
    error,
    me,
    meLoading,
    refresh,
    connect,
    disconnect,
    generateOnboardingLink,
  };
}
